import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
    AfterViewInit,
    Component,
    computed,
    effect,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    inject,
    Injector,
    Input,
    input,
    model,
    OnDestroy,
    OnInit,
    Output,
    runInInjectionContext,
    signal,
    ViewEncapsulation,
} from '@angular/core';
import { DraggableEnpsComponent } from '@common/components/base/draggable-enps-component';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { CARD_WIDTH_LOWER_BREAKPOINTS, PST_PREFIX } from '@common/enums/constants';
import { CardViewType, FieldDefValidations } from '@common/enums/enums';
import { BasicContentService } from '@common/services/basic-content-service';
import { ExternalActionsService } from '@common/services/external-actions-service';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { LanguageSettings } from '@common/services/language-settings';
import { LayoutState } from '@common/services/layout-state';
import { NavigationService } from '@common/services/navigation-service';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { VisualStateService } from '@common/services/visual-state-service';
import { Store } from '@ngrx/store';
import { PlaybookService } from '@playbook/services/playbook-service';
import { DateTime } from '@src/suite/common/data-models/complex-types/date-time';
import { HighlightPipe } from '@src/suite/common/pipes/highlight';
import { selectAssignmentIsSelected } from '@src/suite/common/store/fullscreen';
import { selectClientTimeZone, selectPersonalTimeZone } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N, selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { selectViewStateSearchString } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { AssignmentRecordActions, RecordHttpActions } from '@src/suite/common/store/records/records.actions';
import { i18nKeys } from '@src/suite/common/utils/i18n.default';
import { getAssignmentSecurityPrivsWrite, getDataTransferString } from '@src/suite/common/utils/record-utils';
import { AppService } from '@suite/app.service';
import { getItemString } from '@suite/common/utils/util';
import { Subscription } from 'rxjs';

@Component({
    selector: 'st-assignment-item',
    templateUrl: './assignment.html',
    styleUrls: ['./assignment.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-assignment-item',
    },
})
export class AssignmentComponent extends DraggableEnpsComponent implements OnInit, AfterViewInit, OnDestroy {
    injector = inject(Injector);
    store = inject(Store);
    appService = inject(AppService);
    globalTablesService = inject(GlobalTablesService);
    layoutState = inject(LayoutState);
    visualStateService = inject(VisualStateService);
    externalActionsService = inject(ExternalActionsService);
    languageSettings = inject(LanguageSettings);
    navigationService = inject(NavigationService);
    basicContentService = inject(BasicContentService);
    nomWebServicesJson = inject(NomWebServicesJson);
    playbookService = inject(PlaybookService);
    highlightPipe = inject(HighlightPipe);
    elmRef = inject(ElementRef);
    CardViewTypes = CardViewType;

    private containerResizeObserver: ResizeObserver;
    public readonly FieldDefValidations = FieldDefValidations;
    public readonly getItemString = getItemString;
    public itemStringCache = new Map<string, string>();
    data = model<StorytellingAssignmentRecord>({});
    isReadOnly = input(false);
    inView = signal(false);
    cardViewType = signal(CardViewType.LARGE);
    i18n = this.store.selectSignal(selectI18N);
    searchString = this.store.selectSignal(selectViewStateSearchString);
    personalTimeZone = this.store.selectSignal(selectPersonalTimeZone);
    clientTimeZone = this.store.selectSignal(selectClientTimeZone);

    isPublication = input(false);
    disabled = input(false, {
        transform: coerceBooleanProperty,
    });
    inSidecarOpenedMode = input(false, { transform: coerceBooleanProperty });

    @Input() public dragEnabled: boolean = true;
    @Output() public assignmentOpenEmitter: EventEmitter<void> = new EventEmitter();
    @Output() public assignmentDeleteEmitter: EventEmitter<number> = new EventEmitter();
    @Output() public deletePublicationEmitter: EventEmitter<any> = new EventEmitter();

    constructor() {
        super();
        effect(() => {
            if (this.data()) {
                this.itemStringCache.clear();
            }
        });
    }

    @HostListener('click')
    onClick() {
        if (this.cardDisabled()) {
            return;
        }
        if (this.cardViewType() === CardViewType.SMALL) {
            this.assignmentOpenEmitter.emit();
        }
    }

    @HostBinding('class.st-assignment-item--selected')
    selected = this.store.selectSignal(selectAssignmentIsSelected(this.data()?.Guid))();

    @HostBinding('class.st-assignment-item__mobile')
    get mobileCard(): boolean {
        return this.visualStateService.isLessThanMedium || (this.inFullscreenMode && this.visualStateService.isLessThanXLarge);
    }

    @Input()
    get inFullscreenMode() {
        return this._inFullscreenMode;
    }
    set inFullscreenMode(value: BooleanInput) {
        this._inFullscreenMode = coerceBooleanProperty(value);
    }
    private _inFullscreenMode = false;

    public get assignmentOwnerProperty(): string {
        return PSTFieldNames.PSTAssignmentOwner;
    }

    currentTimeZone = computed(() => {
        return this.personalTimeZone() ? this.personalTimeZone() : this.clientTimeZone();
    });

    attachmentsFieldGroupLabel = computed(() => {
        const attachmentsFieldGroupLabel = 'PSTWebFieldGroup';

        return this.store
            .selectSignal(selectTranslatedKey(`${attachmentsFieldGroupLabel}_${i18nKeys.PSTWebAttachmentsPane}`))()
            .includes(attachmentsFieldGroupLabel)
            ? this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebAttachmentsPane))()
            : this.store.selectSignal(selectTranslatedKey(`${attachmentsFieldGroupLabel}_${i18nKeys.PSTWebAttachmentsPane}`))();
    });

    get timeUpdatedLabel(): string {
        return `${this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebChangedAgoLabel))()} ${this.data()?.ModTime?.moment?.fromNow()}`;
    }

    get updatedBy(): string {
        return `${this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebChangedByLabel))()} ${this.globalTablesService.Staff[this?.data()?.ModBy]?.DisplayName}`;
    }

    get timeUpdatedByLabel() {
        return `${this.timeUpdatedLabel} ${this.updatedBy}`;
    }

    get showFullCard(): boolean {
        return !(this.visualStateService.isLessThanMedium || (this.inFullscreenMode && this.visualStateService.isLessThanXLarge));
    }

    enforceRequiredFieldsOnCreate = computed(() => {
        return this.globalTablesService.EnforceRequiredFieldsOnCreate;
    });

    editable = computed(() => {
        return getAssignmentSecurityPrivsWrite(this.data()) && !this.data()?.Path?.isWasteBinPath;
    });

    cardDisabled = computed(() => {
        return this.isReadOnly() || !this.editable() || this.disabled();
    });

    deskList = computed(() => {
        return this.globalTablesService.getListItemsForDesk(this.data()?.Type);
    });

    displayOpenActionButtonHoverTitle = computed(() => {
        return this.globalTablesService.getDisplayOpenActionButtonHoverTitle(this.inSidecarOpenedMode())();
    });

    public hover: boolean = false;
    public recordChangesSub: Subscription;
    public dragData: string = '';
    sub: any;

    public pstFieldNames = PSTFieldNames;

    async ngOnInit() {
        setTimeout(() => {
            this.inView.set(true);
        }, 0);
        runInInjectionContext(this.injector, () => {
            effect(
                () => {
                    if (this.data()?.Guid) {
                        this.selected = this.store.selectSignal(selectAssignmentIsSelected(this.data().Guid))();
                    }
                },
                { allowSignalWrites: true }
            );
        });
    }

    public async onENPSObjectDrop(data: any) {
        if (data instanceof FileList) {
            const attachments = [];
            for (let i = 0; i < data.length; i++) {
                const shouldProceed = await this.appService.shouldProceedAttachment(data[i].name, data[i].size);
                if (!shouldProceed) return;
                const attachment = data[i];
                attachments.push({ file: attachment, finished: false, name: attachment.name });
            }
            const base64Files = await this.appService.readUrl(attachments);
            for (const file of base64Files) {
                try {
                    file.ExternalFiles = this.data().ExternalFiles;
                    await this.appService.attachmentService.createAttachment(this.data().Database, this.data().Path.fullPath, this.data().Guid, false, file, this.data());
                } catch (ex) {
                    console.log(ex);
                }
            }
        }
    }

    public dragStartHandler($event) {
        $event.dataTransfer.setData('text/plain', PST_PREFIX + JSON.stringify(getDataTransferString(this.data())));
        $event.stopPropagation();
    }

    updateAssignment(value: any, fieldId: string) {
        if (this.cardDisabled()) return;
        // TODO: not cool to have this title logic here
        const fieldDef = [{ value, key: fieldId }];
        if (fieldId === PSTFieldNames.Title) {
            if (!value) return;
            this.data().Title = value.trim();
            this.data().Slug = value.trim();
            fieldDef.push({ value: value, key: PSTFieldNames.Slug });
        }
        this.store.dispatch(RecordHttpActions.saveRecordChanges({ record: this.data(), fieldsToUpdate: fieldDef }));
    }

    timezoneChanged(fieldId: string, event: DateTime) {
        this.data.update(val => {
            val[fieldId] = event;
            return val;
        });
        this.store.dispatch(AssignmentRecordActions.updateAssignments({ records: [this.data()] }));
    }

    ngAfterViewInit() {
        this.containerResizeObserver = new ResizeObserver(entries => {
            const width = entries[0].contentRect.width;
            if (width < CARD_WIDTH_LOWER_BREAKPOINTS.medium) {
                this.cardViewType.set(CardViewType.SMALL);
            } else if (width < CARD_WIDTH_LOWER_BREAKPOINTS.large) {
                this.cardViewType.set(CardViewType.MEDIUM);
            } else {
                this.cardViewType.set(CardViewType.LARGE);
            }
        });

        this.containerResizeObserver.observe(this.elmRef.nativeElement);
    }

    ngOnDestroy(): void {
        this.containerResizeObserver.unobserve(this.elmRef.nativeElement);
        this.containerResizeObserver.disconnect();
    }

    public deletePublication(event: any) {
        event.stopPropagation();
        this.deletePublicationEmitter.emit();
    }

    getInnerProp(item: FieldDef) {
        const val = this.data()?.[item.ID.split('-')[0]];
        if (val) return val[item.ComplexPropInnerId];
        return '';
    }
}
